import {
  Box,
  Checkbox,
  CollapsibleCard,
  DatePicker,
  Divider,
  FormLabel,
  Grid,
  Skeleton,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@atgfr8/atg-ui-tailwind';
import { Notification, RadioGroup, SelectInput } from '@components';
import { useParams } from '@solidjs/router';
import {
  ContractTermOption,
  CustomerContract,
  customerContractStore,
  getCustomerContracts,
  InsuranceOption,
  setCustomerContract,
} from '@store/customers/customerDetails/contracts';
import { formatDataTestId } from '@utils/dataTestIdFormatter';
import { isReadonly } from '@utils/utils';
import { createEffect, For, onMount, Show } from 'solid-js';

import cls from './classes';
import {
  accessorialOptions,
  amountOptions,
  brokerInsuranceOptions,
  brokerResponsibilityOptions,
  cargoOptions,
  carrierBrokerLiabilityOptions,
  carrierInsuranceOptions,
  claimAndConcealedDamageOptions,
  claimAndConcealedDamageRadioOptions,
  claimValueBasisOptions,
  contractPaymentTerms,
  contractTerms,
  countries,
  entities,
  indemnificationOptions,
  otherCarrierRequirementsOptions,
  pollutionOptions,
  temperatureControlledOptions,
  termAndPaymentTextAreaOptions,
  timeFrameOptions,
  workerCompensationOptions,
} from './constant';

const DisclaimerText = (
  <span>
    <b>DISCLAIMER</b>: THE CUSTOMER'S REQUIREMENTS ARE OUTLINED BELOW. IT IS THE
    AGENT'S RESPONSIBILITY TO ENSURE ALL CARRIERS MEET THE COVERAGE LIMITS
    REQUIRED AND THAT THE CUSTOMER’S DEMANDS ARE OTHERWISE MET. THIS DOCUMENT IS
    MEANT TO ASSIST, BUT IS NOT A SUPPLEMENT FOR REVIEWING THE ENTIRE AGREEMENT.
  </span>
);

interface CheckBoxComponentProps {
  label: string;
  key: string;
  optionType: string;
  value: InsuranceOption;
  handleCheckBoxComponentChange: (
    key: InsuranceOptionKeys,
    value: string | boolean,
    intermediatryVal: keyof InsuranceOption,
  ) => void;
}

type InsuranceOptionKeys =
  | 'carrierGeneralLiability'
  | 'carrierAutoLiability'
  | 'bipd'
  | 'carrierCyber'
  | 'generalLiability'
  | 'generalAuto'
  | 'generalCyber'
  | 'primaryCargo'
  | 'refrigeratedCargo'
  | 'contingentCargo'
  | 'carrierEnv'
  | 'generalEnv';

const CheckBoxComponent = (props: CheckBoxComponentProps) => {
  if (customerContractStore.customerContract === null) return null;
  let menuItems = amountOptions;
  switch (props.optionType) {
    case 'amount':
      menuItems = amountOptions;
      break;
    case 'pollution':
      menuItems = pollutionOptions;
      break;
    case 'cargo':
      menuItems = cargoOptions;
      break;
    case 'worker':
      menuItems = workerCompensationOptions;
      break;
  }

  return (
    <Grid container class="mb-4 items-start">
      <Grid size={6} class="min-w-0 pr-2">
        <Checkbox
          size="xs"
          color="info"
          label={props.label}
          class="w-full max-w-full [&>span]:whitespace-normal! [&>span]:wrap-break-word!"
          checked={props.value.enabled}
          onChange={(val) => {
            props.handleCheckBoxComponentChange(
              props.key as InsuranceOptionKeys,
              val,
              'enabled',
            );
            if (!val) {
              props.handleCheckBoxComponentChange(
                props.key as InsuranceOptionKeys,
                '',
                'optionsValue',
              );
              props.handleCheckBoxComponentChange(
                props.key as InsuranceOptionKeys,
                '',
                'otherValue',
              );
            }
          }}
          disabled={isReadonly()}
          dataTestId={`contracts-${props.key}-checkboxInput`}
        />
      </Grid>
      <Grid size={6}>
        <Stack spacing={1}>
          <SelectInput
            label=""
            placeholder="Select Amount"
            value={
              props.value.enabled ? String(props.value.optionsValue || '') : ''
            }
            onChange={(value) => {
              props.handleCheckBoxComponentChange(
                props.key as InsuranceOptionKeys,
                value,
                'optionsValue',
              );
            }}
            menuItems={menuItems.map(
              (item: { value: string; label: string }) => ({
                label: item.label,
                value: item.value,
              }),
            )}
            disabled={!Boolean(props.value.enabled) || isReadonly()}
            dataTestId={formatDataTestId(
              `Insurance amount ${props.key}`,
              'selectInput',
            )}
          />
          <div
            class={`${
              props.value.enabled
                ? props.value.optionsValue == '-1' ||
                  props.value.optionsValue == '_other'
                  ? 'block'
                  : 'hidden'
                : 'hidden'
            }`}
          >
            <TextField
              label=""
              showInCurrencyFormat
              type="number"
              value={props.value.otherValue ?? ''}
              onChange={(val: string) => {
                props.handleCheckBoxComponentChange(
                  props.key as InsuranceOptionKeys,
                  val,
                  'otherValue',
                );
              }}
              maxLength={16}
              disabled={isReadonly()}
              dataTestId={`contracts-${props.key}-other-value-textInput`}
            />
          </div>
        </Stack>
      </Grid>
    </Grid>
  );
};

interface RadioComponentProps {
  label: string;
  key: string;
  value: boolean | null;
  onRadioChange: (val: string, key: keyof CustomerContract) => void;
}

const RadioComponent = (props: RadioComponentProps) => {
  return (
    <Grid container class="mb-2 items-center">
      <Grid item size={6}>
        <Box class={cls.labelClass}>{props.label}</Box>
      </Grid>
      <Grid item size={6} class="flex!">
        <RadioGroup
          class="ml-auto!"
          value={props.value === null ? '' : String(props.value)}
          onChange={(value) => {
            if (!isReadonly()) {
              props.onRadioChange(value, props.key as keyof CustomerContract);
            }
          }}
          options={[
            { label: 'Yes', value: 'true' },
            { label: 'No', value: 'false' },
            { label: 'Not Specified', value: '' },
          ]}
        />
      </Grid>
    </Grid>
  );
};

interface SelectComponentProps {
  label: string;
  key: string;
  value: ContractTermOption | string | null;
  onlySelect?: boolean;
  handleChange: (
    value: ContractTermOption | string,
    key: keyof CustomerContract,
  ) => void;
}

const SelectComponetWithLabel = (props: SelectComponentProps) => {
  const menuItems = Boolean(props.onlySelect)
    ? claimValueBasisOptions
    : timeFrameOptions;

  const selectValue = () => {
    if (props.onlySelect) {
      return typeof props.value === 'string' ? props.value : '';
    }
    if (props.value == null || typeof props.value !== 'object') {
      return '';
    }
    return (props.value as ContractTermOption).optionsValue ?? '';
  };

  const handleOnChange = (value: string) => {
    if (Boolean(props.onlySelect)) {
      props.handleChange(value, props.key as keyof CustomerContract);
    } else {
      props.handleChange(
        { optionsValue: value, otherValue: null },
        props.key as keyof CustomerContract,
      );
    }
  };
  return (
    <FormLabel>
      <Box class={`${cls.labelClass} mb-2`}>{props.label}</Box>
      <Stack>
        <SelectInput
          label=""
          placeholder={
            Boolean(props.onlySelect) ? 'Select Value' : 'Select Timeframe'
          }
          value={selectValue()}
          onChange={(value) => {
            handleOnChange(value);
            if (!props.onlySelect && value !== '_other') {
              props.handleChange(
                { optionsValue: value, otherValue: '' },
                props.key as keyof CustomerContract,
              );
            }
          }}
          menuItems={menuItems.map((item) => ({
            label: item.label,
            value: item.value,
          }))}
          disabled={isReadonly()}
          style={{ width: '60%' }}
          dataTestId={formatDataTestId(
            `${props.label} timeframe`,
            'selectInput',
          )}
        />
        <Show
          when={
            !props.onlySelect &&
            props.value != null &&
            typeof props.value === 'object' &&
            (props.value as ContractTermOption).optionsValue === '_other'
          }
        >
          <TextField
            value={(props.value as ContractTermOption).otherValue ?? ''}
            onChange={(val: string) => {
              props.handleChange(
                { optionsValue: '_other', otherValue: val as string },
                props.key as keyof CustomerContract,
              );
            }}
            size="sm"
            style={{ width: '60%' }}
            disabled={isReadonly()}
            dataTestId={`contracts-${props.key}-other-value-textInput`}
          />
        </Show>
      </Stack>
    </FormLabel>
  );
};

interface TextOnlyComponentProps {
  label: string;
  key1: keyof CustomerContract;
  key2: keyof CustomerContract;
  value1: boolean;
  value2: string;
  handleTextOnlyComponentChange: (
    key: keyof CustomerContract,
    value: boolean | string,
  ) => void;
}

const TextOnlyComponent = (props: TextOnlyComponentProps) => {
  return (
    <Grid container class="mb-4">
      <Grid size={6}>
        <Checkbox
          size="xs"
          color="info"
          label={props.label}
          checked={props.value1 || false}
          onChange={(val) => {
            props.handleTextOnlyComponentChange(
              props.key1 as keyof CustomerContract,
              val,
            );
            if (!val) {
              props.handleTextOnlyComponentChange(
                props.key2 as keyof CustomerContract,
                '',
              );
            }
          }}
          disabled={isReadonly()}
          dataTestId={`contracts-${props.key1}-checkboxInput`}
        />
      </Grid>
      <Grid size={6}>
        <TextField
          showInCurrencyFormat={props.label === 'Other'}
          size="sm"
          label=""
          type="number"
          value={Boolean(props.value1) ? props.value2 : ''}
          placeholder="Enter Amount"
          onChange={(val: string) => {
            props.handleTextOnlyComponentChange(
              props.key2 as keyof CustomerContract,
              val,
            );
          }}
          maxLength={16}
          disabled={!props.value1 || isReadonly()}
          dataTestId={`contracts-${props.key2}-textInput`}
        />
      </Grid>
    </Grid>
  );
};

export const Contracts = () => {
  const params = useParams();

  onMount(async () => {
    if (params.id) {
      setCustomerContract('isLoading', true);
      await getCustomerContracts(params.id);
    }
  });
  let previousId = params.id;
  createEffect(async () => {
    if (previousId !== params.id) {
      previousId = params.id;
      setCustomerContract('isLoading', true);
      params.id && (await getCustomerContracts(params.id));
    }
  });
  const getContractsValuesFromKey = (key: keyof CustomerContract) => {
    return customerContractStore.customerContract
      ? customerContractStore.customerContract[key]
      : null;
  };

  const handleSelectComponentChange = (
    value: ContractTermOption | string,
    key: keyof CustomerContract,
  ) => {
    setCustomerContract('customerContract', key, value);
  };

  const renderConcealedDamageOptions = () => {
    if (!customerContractStore.customerContract) return null;
    const leftSide = claimAndConcealedDamageOptions
      .slice(0, 3)
      .map((itm) => (
        <SelectComponetWithLabel
          {...itm}
          value={
            getContractsValuesFromKey(
              itm.key as keyof CustomerContract,
            ) as ContractTermOption
          }
          handleChange={handleSelectComponentChange}
        />
      ));
    const rightSide = claimAndConcealedDamageOptions
      .slice(3, 6)
      .map((itm) => (
        <SelectComponetWithLabel
          {...itm}
          value={
            getContractsValuesFromKey(
              itm.key as keyof CustomerContract,
            ) as ContractTermOption
          }
          handleChange={handleSelectComponentChange}
        />
      ));

    return (
      <Grid container spacing={2} class="mb-3!">
        <Grid item size={6}>
          <Stack spacing={2}>{leftSide}</Stack>
        </Grid>
        <Grid item size={6}>
          <Stack spacing={2}>{rightSide}</Stack>
        </Grid>
      </Grid>
    );
  };

  const handleRadioChange = (val: string, key: keyof CustomerContract) => {
    const value = val === 'true' ? true : val === 'false' ? false : null;
    setCustomerContract('customerContract', key, value);
  };

  const onCheckBoxComponentChange = (
    key: InsuranceOptionKeys,
    value: string | boolean,
    intermediatryVal: keyof InsuranceOption,
  ) => {
    setCustomerContract('customerContract', key, intermediatryVal, value);
  };

  return (
    <>
      <Box class="w-full p-3">
        <Show
          when={!customerContractStore.isLoading}
          fallback={
            <Stack spacing={2}>
              <Skeleton variant="rectangular" height={60} />
              <Skeleton variant="rectangular" height={60} />
            </Stack>
          }
        >
          <Notification type="info" text={DisclaimerText} />
          <Box class="p-2 border-[#31708f] border w-full mt-4 rounded-xs flex ">
            <FormLabel class="pr-[50px]! border-r-2! mr-[50px]">
              <Box class={cls.labelClass}>Agreement Effective Date</Box>
              <DatePicker
                value={
                  (getContractsValuesFromKey('agreementDate') as string) || ''
                }
                handleChange={(val: string) => {
                  setCustomerContract('customerContract', 'agreementDate', val);
                }}
                disabled={isReadonly()}
                dataTestId="contracts-agreement-date-datePicker"
                class="[&>.MuiFormControl-root]:p-0!"
              />
            </FormLabel>
            <FormLabel class="pr-[50px]! border-r-2! mr-[50px]">
              <Box class={cls.labelClass}>Customer Entity Type</Box>
              <Stack>
                <SelectInput
                  label=""
                  value={
                    customerContractStore.customerContract?.customerEntityType
                      .optionsValue ?? ''
                  }
                  placeholder="Select Entity Type"
                  menuItems={entities.map((item) => ({
                    label: item.label,
                    value: String(item.value),
                  }))}
                  onChange={(value) => {
                    setCustomerContract(
                      'customerContract',
                      'customerEntityType',
                      'optionsValue',
                      value,
                    );
                    if (value !== '_other') {
                      setCustomerContract(
                        'customerContract',
                        'customerEntityType',
                        'otherValue',
                        '',
                      );
                    }
                  }}
                  disabled={isReadonly()}
                  style={{ width: '100%' }}
                  dataTestId={formatDataTestId(
                    'Customer Entity Type',
                    'selectInput',
                  )}
                />
                <Show
                  when={
                    customerContractStore.customerContract?.customerEntityType
                      .optionsValue === '_other'
                  }
                >
                  <TextField
                    size="sm"
                    label=""
                    value={
                      customerContractStore.customerContract?.customerEntityType
                        .otherValue ?? ''
                    }
                    onChange={(val: string) => {
                      setCustomerContract(
                        'customerContract',
                        'customerEntityType',
                        'otherValue',
                        val,
                      );
                    }}
                    disabled={isReadonly()}
                    dataTestId="contracts-customer-entity-type-other-value-textInput"
                  />
                </Show>
              </Stack>
            </FormLabel>
            <Box class="flex! flex-col! pr-[24px]!">
              <Box class={cls.labelClass}>Is customer a US entity ?</Box>
              <span>
                No
                <Switch
                  dataTestId="contracts-is-usa-entity-switch"
                  checked={customerContractStore.customerContract?.isUsaEntity}
                  onChange={(val) => {
                    setCustomerContract('customerContract', 'isUsaEntity', val);
                  }}
                  disabled={isReadonly()}
                />
                Yes
              </span>
            </Box>
            <FormLabel>
              <Box class={cls.labelClass}>Country of Incorporation</Box>
              <SelectInput
                label=""
                placeholder="Select Country"
                value={getContractsValuesFromKey('countryOption') as string}
                menuItems={countries.map((item) => ({
                  label: item.label,
                  value: String(item.value),
                }))}
                onChange={(value) => {
                  setCustomerContract(
                    'customerContract',
                    'countryOption',
                    value,
                  );
                }}
                disabled={isReadonly()}
                dataTestId={formatDataTestId(
                  'Country of Incorporation',
                  'selectInput',
                )}
              />
            </FormLabel>
          </Box>
        </Show>
        <Grid container spacing={2} class="mt-2!">
          <Grid item size={{ sm: 12, md: 7 }} class="px-2">
            <CollapsibleCard
              startTitle="Insurance"
              loading={customerContractStore.isLoading}
              class="mb-2"
            >
              <Box>
                <Stack spacing={1}>
                  <Box class={cls.labelClass}>Is shipper self insured?</Box>
                  <span>
                    No
                    <Switch
                      dataTestId="contracts-is-shipper-self-insured-switch"
                      checked={
                        customerContractStore.customerContract
                          ?.isShipperSelfInsured
                      }
                      onChange={(val) => {
                        setCustomerContract(
                          'customerContract',
                          'isShipperSelfInsured',
                          val,
                        );
                      }}
                      disabled={isReadonly()}
                    />
                    Yes
                  </span>
                </Stack>
              </Box>
              <Grid container spacing={2}>
                <Grid item size={6} class="my-3">
                  <Stack spacing={2}>
                    <Box>
                      <Box class={cls.labelClass}>Carrier Insurance</Box>
                      <Grid container class="items-center">
                        <Grid item size={6}>
                          <Box class={cls.heading}>All-Risk (Required)</Box>
                        </Grid>
                        <Grid item size={6}>
                          <span>
                            No
                            <Switch
                              dataTestId="contracts-carrier-all-risk-switch"
                              checked={
                                customerContractStore.customerContract
                                  ?.carrierAllRiskEnabled
                              }
                              onChange={(val) => {
                                setCustomerContract(
                                  'customerContract',
                                  'carrierAllRiskEnabled',
                                  val,
                                );
                              }}
                              disabled={isReadonly()}
                            />
                            Yes
                          </span>
                        </Grid>
                      </Grid>
                    </Box>
                    <Box display="flex">
                      <Typography
                        variant="body1"
                        component="p"
                        class={`${cls.labelClass} flex-1!`}
                      >
                        Insurance Type
                      </Typography>
                      <Typography
                        variant="body1"
                        component="p"
                        class={`${cls.labelClass} flex-1!`}
                      >
                        Amount
                      </Typography>
                    </Box>
                  </Stack>
                </Grid>
                <Grid item size={6} class="my-3">
                  <Stack spacing={2}>
                    <Box>
                      <Box class={cls.labelClass}>Broker Insurance</Box>
                      <Grid container class="items-center">
                        <Grid item size={6}>
                          <Box class={cls.heading}>All-Risk (Required)</Box>
                        </Grid>
                        <Grid item size={6}>
                          <span>
                            No
                            <Switch
                              dataTestId="contracts-broker-all-risk-switch"
                              checked={
                                customerContractStore.customerContract
                                  ?.brokerAllRiskEnabled
                              }
                              onChange={(val) => {
                                setCustomerContract(
                                  'customerContract',
                                  'brokerAllRiskEnabled',
                                  val,
                                );
                              }}
                              disabled={isReadonly()}
                            />
                            Yes
                          </span>
                        </Grid>
                      </Grid>
                    </Box>
                    <Box display="flex">
                      <Typography
                        variant="body1"
                        component="p"
                        class={`${cls.labelClass} flex-1!`}
                      >
                        Insurance Type
                      </Typography>
                      <Typography
                        variant="body1"
                        component="p"
                        class={`${cls.labelClass} flex-1!`}
                      >
                        Amount
                      </Typography>
                    </Box>
                  </Stack>
                </Grid>
                <Grid item size={6}>
                  <For
                    each={
                      customerContractStore.customerContract &&
                      carrierInsuranceOptions
                    }
                  >
                    {(item) => (
                      <CheckBoxComponent
                        {...item}
                        value={
                          getContractsValuesFromKey(
                            item.key as keyof CustomerContract,
                          ) as InsuranceOption
                        }
                        handleCheckBoxComponentChange={
                          onCheckBoxComponentChange
                        }
                      />
                    )}
                  </For>
                  <TextOnlyComponent
                    key1="carrierOtherInsuranceEnabled"
                    key2="carrierOtherInsurance"
                    label="Other"
                    value1={
                      customerContractStore.customerContract
                        ?.carrierOtherInsuranceEnabled as boolean
                    }
                    value2={
                      customerContractStore.customerContract
                        ?.carrierOtherInsurance as string
                    }
                    handleTextOnlyComponentChange={(
                      key: keyof CustomerContract,
                      value: string | boolean,
                    ) => {
                      setCustomerContract('customerContract', key, value);
                    }}
                  />
                </Grid>
                <Grid item size={6}>
                  <For
                    each={
                      customerContractStore.customerContract &&
                      brokerInsuranceOptions
                    }
                  >
                    {(item) => (
                      <CheckBoxComponent
                        {...item}
                        value={
                          getContractsValuesFromKey(
                            item.key as keyof CustomerContract,
                          ) as InsuranceOption
                        }
                        handleCheckBoxComponentChange={
                          onCheckBoxComponentChange
                        }
                      />
                    )}
                  </For>
                  <TextOnlyComponent
                    key1="generalOtherInsuranceEnabled"
                    key2="generalOtherInsurance"
                    label="Other"
                    value1={
                      customerContractStore.customerContract
                        ?.generalOtherInsuranceEnabled as boolean
                    }
                    value2={
                      customerContractStore.customerContract
                        ?.generalOtherInsurance as string
                    }
                    handleTextOnlyComponentChange={(
                      key: keyof CustomerContract,
                      value: string | boolean,
                    ) => {
                      setCustomerContract('customerContract', key, value);
                    }}
                  />
                </Grid>
              </Grid>
            </CollapsibleCard>
            <CollapsibleCard
              startTitle="Term and Payment Terms"
              loading={customerContractStore.isLoading}
              class="mb-2"
            >
              <Box class="flex flex-wrap" gap={2} mb={2}>
                <Box flex={1}>
                  <Box class={cls.labelClass}>Contract Term</Box>
                  <SelectInput
                    label=""
                    placeholder="Select Contract Term"
                    menuItems={contractTerms.map((item) => ({
                      label: item.label,
                      value: String(item.value),
                    }))}
                    value={
                      customerContractStore.customerContract
                        ?.contractTermTimeframe.optionsValue ?? ''
                    }
                    onChange={(value) => {
                      setCustomerContract(
                        'customerContract',
                        'contractTermTimeframe',
                        'optionsValue',
                        value,
                      );
                      if (value !== '_other') {
                        setCustomerContract(
                          'customerContract',
                          'contractTermTimeframe',
                          'otherValue',
                          '',
                        );
                      }
                    }}
                    disabled={isReadonly()}
                    style={{ width: '60%', 'margin-top': '10px' }}
                    dataTestId={formatDataTestId(
                      'Contract Term',
                      'selectInput',
                    )}
                  />
                  <Show
                    when={
                      customerContractStore.customerContract
                        ?.contractTermTimeframe.optionsValue === '_other'
                    }
                  >
                    <TextField
                      style={{ width: '60%' }}
                      value={
                        customerContractStore.customerContract
                          ?.contractTermTimeframe.otherValue ?? ''
                      }
                      onChange={(val: string) => {
                        setCustomerContract(
                          'customerContract',
                          'contractTermTimeframe',
                          'otherValue',
                          val,
                        );
                      }}
                      size="sm"
                      disabled={isReadonly()}
                      dataTestId="contracts-contract-term-timeframe-other-value-textInput"
                    />
                  </Show>
                </Box>
                <Box flex={1}>
                  <Box class={cls.labelClass}>Payment Terms</Box>
                  <SelectInput
                    placeholder="Select Timeframe"
                    label=""
                    menuItems={contractPaymentTerms.map((item) => ({
                      label: item.label,
                      value: String(item.value),
                    }))}
                    value={
                      customerContractStore.customerContract?.paymentTermOption
                        ?.optionsValue ?? ''
                    }
                    onChange={(value) => {
                      setCustomerContract(
                        'customerContract',
                        'paymentTermOption',
                        'optionsValue',
                        value,
                      );
                      if (value !== '_other') {
                        setCustomerContract(
                          'customerContract',
                          'paymentTermOption',
                          'otherValue',
                          '',
                        );
                      }
                    }}
                    disabled={isReadonly()}
                    style={{ width: '60%', 'margin-top': '10px' }}
                    dataTestId={formatDataTestId(
                      'Payment Terms',
                      'selectInput',
                    )}
                  />
                  <Show
                    when={
                      customerContractStore.customerContract?.paymentTermOption
                        ?.optionsValue === '_other'
                    }
                  >
                    <TextField
                      value={
                        customerContractStore.customerContract
                          ?.paymentTermOption?.otherValue ?? ''
                      }
                      onChange={(val: string) => {
                        setCustomerContract(
                          'customerContract',
                          'paymentTermOption',
                          'otherValue',
                          val,
                        );
                      }}
                      size="sm"
                      style={{ width: '60%' }}
                      disabled={isReadonly()}
                      dataTestId="contracts-payment-term-option-other-value-textInput"
                    />
                  </Show>
                </Box>
              </Box>
            </CollapsibleCard>
            <CollapsibleCard
              startTitle="Claims and Concealed Damages"
              loading={customerContractStore.isLoading}
              class="mb-2"
            >
              {renderConcealedDamageOptions()}
              <For each={claimAndConcealedDamageRadioOptions}>
                {(item) => (
                  <RadioComponent
                    label={item.label}
                    value={
                      getContractsValuesFromKey(
                        item.key as keyof CustomerContract,
                      ) as boolean | null
                    }
                    key={item.key}
                    onRadioChange={handleRadioChange}
                  />
                )}
              </For>
            </CollapsibleCard>
            <CollapsibleCard
              startTitle="Additional Language"
              loading={customerContractStore.isLoading}
              class="mb-2"
            >
              <For each={termAndPaymentTextAreaOptions}>
                {(item) => (
                  <Box class="mb-3!">
                    <FormLabel>
                      <span class={cls.heading}>{item.label}</span>
                      <Divider class="mb-1!" />
                      <TextField
                        multiline
                        value={
                          (getContractsValuesFromKey(
                            item.key as keyof CustomerContract,
                          ) as string) || ''
                        }
                        onChange={(val) =>
                          setCustomerContract(
                            'customerContract',
                            item.key as keyof CustomerContract,
                            val,
                          )
                        }
                        rows={5}
                        placeholder="Add Details"
                        disabled={isReadonly()}
                        maxLength={4000}
                        dataTestId={`contracts-${item.key}-textAreaField`}
                      />
                    </FormLabel>
                  </Box>
                )}
              </For>
            </CollapsibleCard>
          </Grid>
          <Grid item size={{ sm: 12, md: 5 }}>
            <CollapsibleCard
              startTitle="Temperature Controlled Commodities"
              loading={customerContractStore.isLoading}
              class="mb-2"
            >
              <For each={temperatureControlledOptions}>
                {(item) => (
                  <RadioComponent
                    label={item.label}
                    value={
                      getContractsValuesFromKey(
                        item.key as keyof CustomerContract,
                      ) as boolean | null
                    }
                    key={item.key}
                    onRadioChange={handleRadioChange}
                  />
                )}
              </For>
            </CollapsibleCard>
            <CollapsibleCard
              startTitle="Other Carrier Requirements"
              loading={customerContractStore.isLoading}
              class="mb-2"
            >
              <For each={otherCarrierRequirementsOptions}>
                {(item) => (
                  <RadioComponent
                    label={item.label}
                    value={
                      getContractsValuesFromKey(
                        item.key as keyof CustomerContract,
                      ) as boolean | null
                    }
                    key={item.key}
                    onRadioChange={handleRadioChange}
                  />
                )}
              </For>
            </CollapsibleCard>
            <CollapsibleCard
              startTitle="Carrier/Broker Liability"
              loading={customerContractStore.isLoading}
              class="mb-2"
            >
              <For each={carrierBrokerLiabilityOptions}>
                {(item) => (
                  <RadioComponent
                    label={item.label}
                    value={
                      getContractsValuesFromKey(
                        item.key as keyof CustomerContract,
                      ) as boolean | null
                    }
                    key={item.key}
                    onRadioChange={handleRadioChange}
                  />
                )}
              </For>
            </CollapsibleCard>
            <CollapsibleCard
              startTitle="Broker Responsibilities"
              loading={customerContractStore.isLoading}
              class="mb-2"
            >
              <For each={brokerResponsibilityOptions}>
                {(item) => (
                  <RadioComponent
                    label={item.label}
                    value={
                      getContractsValuesFromKey(
                        item.key as keyof CustomerContract,
                      ) as boolean | null
                    }
                    key={item.key}
                    onRadioChange={handleRadioChange}
                  />
                )}
              </For>
            </CollapsibleCard>
            <CollapsibleCard
              startTitle="Accessorial/Billing Information"
              loading={customerContractStore.isLoading}
              class="mb-2"
            >
              <For each={accessorialOptions}>
                {(item) => (
                  <RadioComponent
                    label={item.label}
                    value={
                      getContractsValuesFromKey(
                        item.key as keyof CustomerContract,
                      ) as boolean | null
                    }
                    key={item.key}
                    onRadioChange={handleRadioChange}
                  />
                )}
              </For>
            </CollapsibleCard>
            <CollapsibleCard
              startTitle="Indemnification"
              loading={customerContractStore.isLoading}
              class="mb-2"
            >
              <For each={indemnificationOptions}>
                {(item) => (
                  <RadioComponent
                    label={item.label}
                    value={
                      getContractsValuesFromKey(
                        item.key as keyof CustomerContract,
                      ) as boolean | null
                    }
                    key={item.key}
                    onRadioChange={handleRadioChange}
                  />
                )}
              </For>
            </CollapsibleCard>
          </Grid>
        </Grid>
      </Box>
    </>
  );
};
